/**
 * f14.3: one actuals batch inside step 7 (design D6), following
 * `cycle-step.html`: the file, what the system did with it, the cycle check,
 * what did not match, the issues, and Commit / Discard.
 *
 * Reuses f14.1's review, rows, commit and discard endpoints. Commit and
 * Discard render only from the server's `capabilities`; the cycle check
 * message is the server's, verbatim.
 */

import { useState } from 'react';
import { Check, FileSpreadsheet, Info, X } from 'lucide-react';

import type { ActualsReview } from '@/api/client';
import { formatWhen, shortChecksum } from '@/routes/data-import/importView';
import { Modal } from '@/routes/reference-data/Modal';
import { EmptyCell } from '@/ui/EmptyCell';

import { formatAmount } from '../../reconciliation/reconciliationFormat';
import { refusalMessage, severityPill } from '../usEngagements/usEngagementsView';
import {
  MATCH_TABS,
  batchStatePill,
  commitSummary,
  matchCount,
  periodLabel,
  processingFacts,
} from './actualsStepView';
import { useActualsRows, useCommitActualsBatch, useDiscardActualsBatch } from './useActualsStep';

export function ActualsBatchReview({
  cycleId,
  cycleCode,
  review,
  onCommitStarted,
}: {
  cycleId: string;
  cycleCode: string;
  review: ActualsReview;
  onCommitStarted: (jobId: string) => void;
}) {
  return (
    <div className="stack">
      <FileCard review={review} />
      <ProcessingCard review={review} />
      <CycleCheck review={review} />
      <UnmatchedCard cycleId={cycleId} review={review} />
      <Actions cycleId={cycleId} cycleCode={cycleCode} review={review} onCommitStarted={onCommitStarted} />
    </div>
  );
}

function FileCard({ review }: { review: ActualsReview }) {
  const { batch } = review;
  const currency = review.import?.currency_code;
  const period = periodLabel(review.import?.period_month);
  return (
    <div className="card">
      <div className="card-head">
        <h3>File dropped</h3>
        <span className={batchStatePill(batch.state.code)}>{batch.state.name}</span>
      </div>
      <div className="card-body">
        <div className="dropzone is-filled">
          <div className="row us-eng-file">
            <FileSpreadsheet className="dz-icon" aria-hidden="true" />
            <div className="us-eng-file-text">
              <div className="dz-title truncate">{batch.original_filename ?? 'The uploaded file'}</div>
              <div className="hint">
                dropped {formatWhen(batch.uploaded_at)} &middot; batch #{batch.batch_number}
                {currency && period ? ` · ${currency} · ${period} (read from the file)` : ''}
              </div>
            </div>
          </div>
        </div>
        <dl className="dl cols-4 mt-4">
          <div>
            <dt>Detected format</dt>
            <dd>
              {batch.detected_format ?? <EmptyCell />} <span className="hint">sniffed by content</span>
            </dd>
          </div>
          <div>
            <dt>Header row</dt>
            <dd>{batch.detected_header_row ?? <EmptyCell />}</dd>
          </div>
          <div>
            <dt>Revenue block</dt>
            <dd>{review.import?.revenue_block.name ?? <EmptyCell />}</dd>
          </div>
          <div>
            <dt>SHA-256</dt>
            <dd className="mono">{shortChecksum(batch.content_sha256)}</dd>
          </div>
        </dl>
        {batch.rejection_reason && (
          <div className="alert alert-danger mt-4" role="alert">
            <div>
              <div className="a-body">{batch.rejection_reason}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProcessingCard({ review }: { review: ActualsReview }) {
  const infos = review.issues.items.filter((issue) => issue.severity === 'INFO');
  return (
    <div className="card">
      <div className="card-head">
        <h3>What the system did with it</h3>
        <span className="hint">
          {review.issues.counts.ERROR ?? 0} error &middot; {review.issues.counts.WARNING ?? 0} warning &middot;{' '}
          {review.issues.counts.INFO ?? 0} info
        </span>
      </div>
      <div className="card-body stack">
        <dl className="dl cols-4">
          {processingFacts(review).map((fact) => (
            <div key={fact.label}>
              <dt>{fact.label}</dt>
              <dd className="mono">{fact.value ?? '—'}</dd>
            </div>
          ))}
        </dl>
        {infos.length > 0 && (
          <ul className="stack-sm us-eng-list">
            {infos.map((issue, index) => (
              <li key={`${issue.code}-${index}`} className="row us-eng-transform">
                <Info className="us-eng-ok" aria-hidden="true" />
                <span>{issue.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CycleCheck({ review }: { review: ActualsReview }) {
  const check = review.cycle;
  if (!check || !check.message) return null;
  const refused = check.period_in_cycle === false || !check.editable;
  return (
    <div className={`alert ${refused ? 'alert-danger' : check.is_closing_month ? 'alert-info' : 'alert-warning'}`}
      role="status">
      <div>
        <div className="a-title">Cycle {check.cycle_code}</div>
        <div className="a-body">
          {check.message}
          {!check.editable ? ' · the cycle is frozen or submitted' : ''}
        </div>
      </div>
    </div>
  );
}

function UnmatchedCard({ cycleId, review }: { cycleId: string; review: ActualsReview }) {
  const [active, setActive] = useState(MATCH_TABS[0]?.status ?? 'ELEMENT_UNKNOWN');
  const tab = MATCH_TABS.find((item) => item.status === active) ?? MATCH_TABS[0];
  const total = MATCH_TABS.reduce((sum, item) => sum + matchCount(review, item.status), 0);
  if (review.batch.state.code === 'REJECTED' || !review.import) return null;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Did not match</h3>
        {total > 0 ? (
          <span className="badge pill-warning">{total} rows</span>
        ) : (
          <span className="badge pill-success">Everything matched</span>
        )}
      </div>
      <div className="card-body stack">
        <div className="tabs" role="tablist" aria-label="Rows that did not match">
          {MATCH_TABS.map((item) => (
            <button
              key={item.status}
              type="button"
              role="tab"
              aria-selected={item.status === active}
              className={item.status === active ? 'tab is-active' : 'tab'}
              onClick={() => setActive(item.status)}
            >
              {item.label} <span className="count">{matchCount(review, item.status)}</span>
            </button>
          ))}
        </div>
        {tab && <p className="hint">{tab.hint}.</p>}
        {tab && matchCount(review, tab.status) > 0 && (
          <UnmatchedRows cycleId={cycleId} batchId={review.batch.id} status={tab.status} />
        )}
      </div>
    </div>
  );
}

function UnmatchedRows({ cycleId, batchId, status }: { cycleId: string; batchId: string; status: string }) {
  const rows = useActualsRows(cycleId, batchId, status);
  if (rows.isPending) return <p className="hint">Loading rows…</p>;
  if (rows.isError) return <p className="hint">{refusalMessage(rows.error)}</p>;
  return (
    <div className="table-scroll">
      <table className="data compact">
        <thead>
          <tr>
            <th>Row</th>
            <th>Unit</th>
            <th>Client</th>
            <th>Engagement</th>
            <th>Element</th>
            <th className="num">Month</th>
          </tr>
        </thead>
        <tbody>
          {rows.data.items.map((row) => (
            <tr key={row.source_row_number}>
              <td className="mono">{row.source_row_number}</td>
              <td>{row.unit_label}</td>
              <td>{row.client_label}</td>
              <td className="mono">{row.engagement_code}</td>
              <td>
                <span className="mono">{row.element_reference}</span>{' '}
                <span className="hint">{row.element_description}</span>
              </td>
              <td className="num">{formatAmount(row.month_amount, row.currency_code ?? '')}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.data.total > rows.data.items.length && (
        <p className="hint">
          Showing {rows.data.items.length} of {rows.data.total} rows.
        </p>
      )}
    </div>
  );
}

function Actions({
  cycleId,
  cycleCode,
  review,
  onCommitStarted,
}: {
  cycleId: string;
  cycleCode: string;
  review: ActualsReview;
  onCommitStarted: (jobId: string) => void;
}) {
  const commit = useCommitActualsBatch(cycleId, review.batch.id);
  const discard = useDiscardActualsBatch(cycleId, review.batch.id);
  const [confirming, setConfirming] = useState(false);
  const { capabilities } = review;
  const errors = review.issues.items.filter((issue) => issue.severity !== 'INFO');

  return (
    <>
      {errors.length > 0 && (
        <div className="card">
          <div className="card-head">
            <h3>Issues</h3>
          </div>
          <div className="card-body">
            <ul className="stack-sm us-eng-list">
              {errors.map((issue, index) => (
                <li key={`${issue.code}-${index}`} className="row us-eng-issue">
                  <span className={severityPill(issue.severity)}>{issue.severity}</span>
                  <span className="us-eng-issue-text">
                    {issue.message}
                    {issue.source_row_number !== null && (
                      <span className="hint"> &middot; row {issue.source_row_number}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {discard.error ? (
        <div className="alert alert-danger" role="alert">
          <div>
            <div className="a-body">{refusalMessage(discard.error)}</div>
          </div>
        </div>
      ) : null}

      {(capabilities.can_commit || capabilities.can_discard) && (
        <div className="card">
          <div className="card-foot">
            <span className="hint">
              Committing stores these actuals as immutable facts and raises the variances Finance decides in
              the reconciliation queue.
            </span>
            <div className="btn-row">
              {capabilities.can_discard && (
                <button type="button" className="btn btn-secondary" disabled={discard.isPending}
                  onClick={() => discard.mutate()}>
                  <X aria-hidden="true" /> Discard
                </button>
              )}
              {capabilities.can_commit && (
                <button type="button" className="btn btn-primary" onClick={() => setConfirming(true)}>
                  <Check aria-hidden="true" /> Commit actuals
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {confirming && (
        <Modal
          title="Commit actuals?"
          subtitle={review.batch.original_filename ?? `batch #${review.batch.batch_number}`}
          onClose={() => {
            setConfirming(false);
            commit.reset();
          }}
          onSubmit={() =>
            commit.mutate(undefined, {
              onSuccess: (accepted) => {
                setConfirming(false);
                onCommitStarted(accepted.job_id);
              },
            })
          }
          busy={commit.isPending}
          submitLabel="Commit actuals"
          submitIcon={<Check aria-hidden="true" />}
          layout="plain"
          message={refusalMessage(commit.error)}
        >
          <p className="mb-4">{commitSummary(review, cycleCode)}</p>
          <div className="alert alert-info">
            <Info aria-hidden="true" />
            <div className="a-body">
              The commit runs as a background job in one transaction &mdash; all of it, or nothing. The step
              completes only when you accept it, once every variance is decided.
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
